import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import EntityPanelSelector, { LAYOUT_MODES } from '@common/components/EntityPanelSelector';
import { loadHosts, loadHostGroups } from '@api/filters';
import * as styles from './HostFilter.module.css';

/**
 * HostFilter Component
 * Uses EntityPanelSelector with two modes:
 * - "By List" mode: 2-panel layout with Filter By dropdown
 * - "Host Group" mode: 3-panel layout (Host Groups → Hosts → Selected, grouped by host group)
 */
function HostFilter({
  selectedHostIds,
  onSelectionChange,
}) {
  const { t } = useTranslation();
  const [filterBy, setFilterBy] = useState('byList'); // 'byList' or 'hostGroup'
  
  // Host Group mode specific state
  const [selectedHostGroupId, setSelectedHostGroupId] = useState(null);
  
  // Track hosts with their parent host group info for grouped display
  // Map: hostId -> { item: host, categoryId: hostGroupId, categoryName: hostGroupName }
  const hostsWithGroupRef = useRef({});

  // Load all hosts using useQuery
  const {
    data: availableHosts = [],
    isLoading: isLoadingHosts,
  } = useQuery({
    queryKey: ['hosts'],
    queryFn: () => loadHosts(),
  });

  // Load all host groups using useQuery
  const {
    data: availableHostGroups = [],
    isLoading: isLoadingHostGroups,
  } = useQuery({
    queryKey: ['hostGroups'],
    queryFn: () => loadHostGroups(),
  });

  // Load hosts for selected host group (only when in hostGroup mode)
  const {
    data: hostsInGroup = [],
    isLoading: loadingHostsInGroup,
  } = useQuery({
    queryKey: ['hostsInGroup', selectedHostGroupId],
    queryFn: () => loadHosts(selectedHostGroupId),
    enabled: filterBy === 'hostGroup' && !!selectedHostGroupId,
  });

  // Cache hosts with their host group info for grouped display
  useEffect(() => {
    if (filterBy !== 'hostGroup' || !selectedHostGroupId || hostsInGroup.length === 0) {
      return;
    }
    const selectedHostGroup = availableHostGroups.find((hg) => hg.id === selectedHostGroupId);
    if (selectedHostGroup) {
      hostsInGroup.forEach((host) => {
        hostsWithGroupRef.current[host.id] = {
          item: host,
          categoryId: selectedHostGroup.id,
          categoryName: selectedHostGroup.name,
        };
      });
    }
  }, [filterBy, selectedHostGroupId, hostsInGroup, availableHostGroups]);

  const loading = isLoadingHosts || isLoadingHostGroups;

  // Handle filter by change
  const handleFilterByChange = useCallback((value) => {
    setFilterBy(value);
    setSelectedHostGroupId(null);
  }, []);

  // Handle host group selection
  const handleHostGroupSelect = useCallback((hostGroupId) => {
    setSelectedHostGroupId(hostGroupId);
  }, []);

  // Filter options for dropdown
  const filterOptions = useMemo(() => [
    { value: 'byList', label: t('reports.filters.byList') },
    { value: 'hostGroup', label: t('reports.filters.byHostGroup') },
  ], [t]);

  // Labels for EntityPanelSelector
  const labels = useMemo(() => ({
    leftPanelTitle: t('reports.filters.availableEntities'),
    middlePanelTitle: t('reports.filters.select'),
    rightPanelTitle: t('reports.filters.selectedEntities'),
    filterByLabel: t('reports.filters.filterBy'),
    selectAll: t('common.selectAll'),
    clearAll: t('common.clearAll'),
    noItemsAvailable: t('reports.filters.noHostsAvailable'),
    noItemsFound: t('reports.filters.noHostsFound'),
    noItemsSelected: t('reports.filters.noHostsSelected'),
    noCategoriesAvailable: t('reports.filters.noHostGroupsAvailable'),
    selectCategoryFirst: t('reports.filters.selectHostGroupFirst'),
  }), [t]);

  // Get selectedItemsWithCategory for grouped display (only in hostGroup mode)
  const selectedItemsWithCategory = useMemo(() => {
    if (filterBy !== 'hostGroup') return null;
    
    // Only return the map entries for selected items
    const result = {};
    selectedHostIds.forEach((hostId) => {
      if (hostsWithGroupRef.current[hostId]) {
        result[hostId] = hostsWithGroupRef.current[hostId];
      }
    });
    return Object.keys(result).length > 0 ? result : null;
  }, [filterBy, selectedHostIds]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>{t('common.loading')}</div>
      </div>
    );
  }

  // Determine layout mode based on filter selection
  const mode = filterBy === 'byList'
    ? LAYOUT_MODES.TWO_COLUMNS_WITH_FILTER
    : LAYOUT_MODES.THREE_COLUMNS_WITH_FILTER;

  return (
    <EntityPanelSelector
      mode={mode}
      availableItems={availableHosts}
      selectedItemIds={selectedHostIds}
      onSelectionChange={onSelectionChange}
      // 3-column mode props
      categories={availableHostGroups}
      selectedCategoryId={selectedHostGroupId}
      onCategorySelect={handleHostGroupSelect}
      middlePanelItems={hostsInGroup}
      middlePanelLoading={loadingHostsInGroup}
      // Grouped display for selected items (only in hostGroup mode)
      selectedItemsWithCategory={selectedItemsWithCategory}
      // Filter dropdown
      filterOptions={filterOptions}
      selectedFilterValue={filterBy}
      onFilterChange={handleFilterByChange}
      // Labels
      labels={labels}
      valueField="id"
    />
  );
}

HostFilter.propTypes = {
  selectedHostIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  onSelectionChange: PropTypes.func.isRequired,
};

HostFilter.defaultProps = {
  selectedHostIds: [],
};

export default HostFilter;
